import { useState } from "react";
import { format } from "date-fns";
import {
  Heart,
  Activity,
  Droplets,
  Wind,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Area,
  AreaChart,
} from "recharts";
import { useVitalsTrendData, useMonitoredPatients } from "@/hooks/useRemoteMonitoring";

export function VitalsTrendChart() {
  const [selectedPatient, setSelectedPatient] = useState<string>("all");
  const [timeRange, setTimeRange] = useState("today");

  const { data: patients, isLoading: patientsLoading } = useMonitoredPatients();
  const { data: vitalsData, isLoading: vitalsLoading } = useVitalsTrendData(
    selectedPatient !== "all" ? selectedPatient : undefined
  );

  const isLoading = patientsLoading || vitalsLoading;

  // Transform data for charts
  const chartData = vitalsData?.map((v, index) => ({
    time: v.recorded_at ? format(new Date(v.recorded_at), "HH:mm") : `P${index}`,
    heartRate: v.heart_rate,
    systolic: v.systolic_bp,
    diastolic: v.systolic_bp ? Math.round(v.systolic_bp * 0.65) : null, // Approximate diastolic
    glucose: v.blood_glucose,
    oxygen: v.oxygen_saturation,
  })) || [];

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-4 w-48" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[400px]" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-primary" />
              Vitals Trends
            </CardTitle>
            <CardDescription>
              Historical vital signs and patterns
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Select value={selectedPatient} onValueChange={setSelectedPatient}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select patient" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Patients</SelectItem>
                {patients?.map((patient) => (
                  <SelectItem key={patient.memberId} value={patient.memberId}>
                    {patient.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder="Time range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="week">This Week</SelectItem>
                <SelectItem value="month">This Month</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {chartData.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Activity className="h-12 w-12 mb-4 opacity-50" />
            <p>No vitals data available</p>
            <p className="text-sm">Data will appear once patients start recording vitals</p>
          </div>
        ) : (
          <Tabs defaultValue="heartRate" className="space-y-4">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="heartRate" className="gap-2">
                <Heart className="h-4 w-4" />
                Heart Rate
              </TabsTrigger>
              <TabsTrigger value="bloodPressure" className="gap-2">
                <Activity className="h-4 w-4" />
                Blood Pressure
              </TabsTrigger>
              <TabsTrigger value="glucose" className="gap-2">
                <Droplets className="h-4 w-4" />
                Glucose
              </TabsTrigger>
              <TabsTrigger value="oxygen" className="gap-2">
                <Wind className="h-4 w-4" />
                SpO₂
              </TabsTrigger>
            </TabsList>

            <TabsContent value="heartRate" className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="heartRateGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--destructive))" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="hsl(var(--destructive))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis 
                    dataKey="time" 
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                  />
                  <YAxis 
                    domain={[50, 120]} 
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                    label={{ value: 'BPM', angle: -90, position: 'insideLeft', style: { fill: 'hsl(var(--muted-foreground))' } }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                  />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="heartRate"
                    name="Heart Rate"
                    stroke="hsl(var(--destructive))"
                    strokeWidth={2}
                    fill="url(#heartRateGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </TabsContent>

            <TabsContent value="bloodPressure" className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis 
                    dataKey="time" 
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                  />
                  <YAxis 
                    domain={[60, 180]} 
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                    label={{ value: 'mmHg', angle: -90, position: 'insideLeft', style: { fill: 'hsl(var(--muted-foreground))' } }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="systolic"
                    name="Systolic"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    dot={{ fill: "hsl(var(--primary))" }}
                  />
                  <Line
                    type="monotone"
                    dataKey="diastolic"
                    name="Diastolic"
                    stroke="hsl(var(--accent))"
                    strokeWidth={2}
                    dot={{ fill: "hsl(var(--accent))" }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </TabsContent>

            <TabsContent value="glucose" className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="glucoseGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--warning))" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="hsl(var(--warning))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis 
                    dataKey="time" 
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                  />
                  <YAxis 
                    domain={[70, 200]} 
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                    label={{ value: 'mg/dL', angle: -90, position: 'insideLeft', style: { fill: 'hsl(var(--muted-foreground))' } }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                  />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="glucose"
                    name="Blood Glucose"
                    stroke="hsl(var(--warning))"
                    strokeWidth={2}
                    fill="url(#glucoseGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </TabsContent>

            <TabsContent value="oxygen" className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="oxygenGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--success))" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="hsl(var(--success))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis 
                    dataKey="time" 
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                  />
                  <YAxis 
                    domain={[90, 100]} 
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                    label={{ value: '%', angle: -90, position: 'insideLeft', style: { fill: 'hsl(var(--muted-foreground))' } }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                  />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="oxygen"
                    name="Oxygen Saturation"
                    stroke="hsl(var(--success))"
                    strokeWidth={2}
                    fill="url(#oxygenGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </TabsContent>
          </Tabs>
        )}
      </CardContent>
    </Card>
  );
}
